/**
 * Factory pour créer le bon adapter d'imprimante
 */

import { PrinterAdapter, PrinterConfig } from './PrinterAdapter';
import { EpsonUSBAdapter } from './EpsonUSBAdapter';
import { EpsonM267DAdapter } from './EpsonM267DAdapter';
import { BrowserPrintAdapter } from './BrowserPrintAdapter';
import {
  HardwareBridgeEpsonAdapter,
  isHardwareBridgeAvailable,
} from './HardwareBridgeAdapter';
import { logger } from '../../utils/logger';

// Cache pour le statut du hardware bridge
let hardwareBridgeAvailable: boolean | null = null;
let hardwareBridgeCheckPromise: Promise<boolean> | null = null;

export class PrinterAdapterFactory {
  /**
   * Vérifier si le Hardware Bridge local est disponible (avec cache)
   */
  static async checkHardwareBridge(): Promise<boolean> {
    if (hardwareBridgeAvailable !== null) {
      return hardwareBridgeAvailable;
    }

    if (hardwareBridgeCheckPromise) {
      return hardwareBridgeCheckPromise;
    }

    hardwareBridgeCheckPromise = isHardwareBridgeAvailable().then((available) => {
      hardwareBridgeAvailable = available;
      hardwareBridgeCheckPromise = null;

      // Invalider le cache après 30 secondes
      setTimeout(() => {
        hardwareBridgeAvailable = null;
      }, 30000);

      return available;
    });

    return hardwareBridgeCheckPromise;
  }

  /**
   * Créer un adapter d'imprimante selon la configuration
   * Version synchrone - utilise le cache du hardware bridge
   */
  static create(config: PrinterConfig): PrinterAdapter {
    // Si le hardware bridge est disponible (vérifié précédemment), l'utiliser
    if (hardwareBridgeAvailable === true) {
      logger.log('Utilisation du Hardware Bridge local pour EPSON');
      return new HardwareBridgeEpsonAdapter();
    }

    // Si modèle EPSON M267D, utiliser l'adapter backend
    if (config.model === 'EPSON_TM_M267D') {
      logger.log('Utilisation de l\'adapter EPSON M267D via backend');
      return new EpsonM267DAdapter();
    }

    // Si connexion USB demandée, utiliser EpsonUSBAdapter
    if (config.connection === 'USB') {
      if (!this.isWebUSBSupported()) {
        logger.warn(
          'Web USB non supporté, utilisation de l\'impression navigateur'
        );
        return new BrowserPrintAdapter();
      }
      return new EpsonUSBAdapter();
    }

    // Pour l'instant, autres connexions pas implémentées
    if (config.connection === 'BLUETOOTH' || config.connection === 'NETWORK') {
      logger.warn(
        `Connexion ${config.connection} non implémentée, utilisation de l\'impression navigateur`
      );
      return new BrowserPrintAdapter();
    }

    // Fallback sur impression navigateur
    return new BrowserPrintAdapter();
  }

  /**
   * Créer un adapter d'imprimante avec détection automatique du Hardware Bridge
   * Version asynchrone - vérifie d'abord le hardware bridge
   */
  static async createAsync(config: PrinterConfig): Promise<PrinterAdapter> {
    // Vérifier si le Hardware Bridge local est disponible
    const bridgeAvailable = await this.checkHardwareBridge();

    if (bridgeAvailable) {
      logger.log('Hardware Bridge local détecté - utilisation pour EPSON');
      return new HardwareBridgeEpsonAdapter();
    }

    // Sinon utiliser la méthode synchrone standard
    return this.create(config);
  }

  /**
   * Vérifier si Web USB est supporté
   */
  static isWebUSBSupported(): boolean {
    return 'usb' in navigator;
  }

  /**
   * Obtenir la liste des modèles supportés
   */
  static getSupportedModels(): Array<PrinterConfig['model']> {
    return [
      'EPSON_TM_T20',
      'EPSON_TM_M30',
      'EPSON_TM_M267D',
      'EPSON_TM_T88',
      'STAR_TSP100',
      'BROWSER_PRINT',
    ];
  }
}
